import { CloudUploadOutlined } from '@ant-design/icons'
import { Button, DatePicker, Form, Input, message, Select, Upload } from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import PageTitle from '../../../components/PageTitle'
import { DATE_FORMAT_LIST } from '../../../constants'
import { Container } from '../../../layouts/Container/Container'
import DashboardLayout from '../../../layouts/DashboardLayout'
import validator from '../../../validator'
import {
  DraggerUploadStyled,
  FormActionFooter,
  ViewDetailContent,
} from './BoardResolutionMinutesUpdateStyled'

const ISSUE_LEVEL_OPTIONS = [
  { label: 'Nghị quyết', value: 'Nghị quyết' },
  { label: 'Quyết định', value: 'Quyết định' },
]

const BoardResolutionMinutesUpdate = props => {
  const { Option } = Select
  const { Dragger } = Upload
  const {
    loadingAnimationStore,
    boardResolutionMinutesStore,
    match,
    fileStore,
    history,
  } = props

  const [form] = Form.useForm()

  const [fileList, setFileList] = useState([])
  const [originalData, setOriginalData] = useState({})

  const { documentId } = match.params

  const handleAddFileToUpload = () => false
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  }

  const handleChangeFile = useCallback(
    info => {
      form.setFieldsValue({
        file_id: info.fileList,
      })
      setFileList(info.fileList)
    },
    [form]
  )

  useEffect(() => {
    loadingAnimationStore.showSpinner(true)
    boardResolutionMinutesStore
      .getBoardResolutionMinutesDetail(documentId)
      .then(
        res => {
          loadingAnimationStore.showSpinner(false)

          const attachments = res.data.attachments || []

          const formData = {
            document_name: res.data.document_name?.trimStart(),
            document_number: res.data.document_number?.trimStart(),
            contruction_board: res.data.contruction_board?.trimStart(),
            issue_date: moment(res.data.issue_date),
            status: res.data.status === 'NEW' ? undefined : res.data.status,
            issue_level: res.data.issue_level?.trimStart(),
            file_id: attachments.map(file => ({
              uid: file.id,
              id: file.id,
              name: file.name,
              status: 'done',
              type: 'application/pdf',
            })),
          }

          form.setFieldsValue(formData)

          const originalFormData = {
            document_name: res.data.document_name?.trimStart() || null,
            document_number: res.data.document_number?.trimStart() || null,
            contruction_board: res.data.contruction_board?.trimStart() || null,
            issue_level: res.data.issue_level?.trimStart() || null,
            status: res.data.status === 'NEW' ? null : res.data.status || null,
            issue_date: res.data.issue_date,
            file_id: attachments.map(file => file.id),
          }

          setOriginalData(originalFormData)

          setFileList(
            attachments.map(file => ({
              uid: file.id,
              id: file.id,
              name: file.name,
              status: 'done',
              type: 'application/pdf',
            }))
          )
        },
        error => {
          message.error(error.vi)
          loadingAnimationStore.showSpinner(false)
        }
      )
    return () => boardResolutionMinutesStore.clearBoardHDQTDetail()
  }, [boardResolutionMinutesStore, documentId, form, loadingAnimationStore])

  const compareData = (originalData, currentValues) => {
    const currentData = {
      ...currentValues,
      status: currentValues.status || null,
    }

    const fieldsToCompare = [
      'document_name',
      'document_number',
      'contruction_board',
      'issue_level',
      'status',
    ]

    for (const field of fieldsToCompare) {
      const originalValue = originalData[field] || null
      const currentValue = currentData[field] || null

      if (originalValue !== currentValue) {
        return false
      }
    }

    if (currentData.issue_date && moment.isMoment(currentData.issue_date)) {
      const currentDateString = currentData.issue_date
        .hour(12)
        .minute(0)
        .second(0)
        .toISOString()

      const originalDate = moment(originalData.issue_date)
        .hour(12)
        .minute(0)
        .second(0)
        .toISOString()

      if (originalDate !== currentDateString) {
        return false
      }
    }

    const currentFileIds = (currentData.file_id || [])
      .filter(file => !file.originFileObj)
      .map(file => file.id)
    const originalFileIds = originalData.file_id || []

    const hasNewFile = (currentData.file_id || []).some(
      file => file.originFileObj
    )

    if (hasNewFile) {
      return false
    }

    if (
      currentFileIds.length !== originalFileIds.length ||
      currentFileIds.some(id => !originalFileIds.includes(id))
    ) {
      return false
    }

    return true
  }

  const handleSubmitForm = async values => {
    if (compareData(originalData, values)) {
      message.warning('Không có giá trị thay đổi')
      return
    }

    loadingAnimationStore.showSpinner(true)

    const submitData = {
      ...values,
      status: values.status || null,
    }
    if (submitData.issue_date && moment.isMoment(submitData.issue_date)) {
      submitData.issue_date = submitData.issue_date
        .hour(12)
        .minute(0)
        .second(0)
        .toISOString()
    }

    const existingFiles = (values.file_id || []).filter(
      file => !file.originFileObj
    )
    const newFiles = (values.file_id || []).filter(file => file.originFileObj)

    if (newFiles.length > 0) {
      const uploadPromises = newFiles.map(file => {
        const formData = new FormData()
        formData.append('file', file.originFileObj)
        return fileStore.uploadFile(formData)
      })

      Promise.all(uploadPromises)
        .then(responses => {
          const newFileIds = responses.map(response => response.data.file_id)
          submitData.file_id = [
            ...existingFiles.map(file => file.id),
            ...newFileIds,
          ]
          handleUpdateBoardHDQT(documentId, submitData)
        })
        .catch(error => {
          message.error(error.vi)
          loadingAnimationStore.showSpinner(false)
        })
    } else {
      submitData.file_id = existingFiles.map(file => file.id)
      handleUpdateBoardHDQT(documentId, submitData)
    }
  }

  const handleUpdateBoardHDQT = useCallback(
    (documentId, submitData) => {
      boardResolutionMinutesStore
        .updateBoardResolutionMinutes(documentId, submitData)
        .then(() => {
          loadingAnimationStore.showSpinner(false)
          message.success(`Cập nhật quyết định nghị quyết thành công`)
          history.push(`/board-resolution-minutes-hdqt/view/${documentId}`)
        })
        .catch(error => {
          loadingAnimationStore.showSpinner(false)
          message.error(error.vi)
        })
    },
    [boardResolutionMinutesStore, history, loadingAnimationStore]
  )

  const handleCancel = () => {
    form.resetFields()
    history.push(`/board-resolution-minutes-hdqt/view/${documentId}`)
  }

  const renderFormContent = () => (
    <ContentBlockWrapper>
      <Container maxWidth={820}>
        <Form
          scrollToFirstError={true}
          {...layout}
          form={form}
          name={'add-vbpc-form'}
          onFinish={handleSubmitForm}>
          <Form.Item
            name={'document_name'}
            label={'Tên văn bản'}
            rules={[
              { required: true, message: 'Vui lòng nhập tên văn bản' },
              { validator: validator.validateInputString },
            ]}>
            <Input placeholder={'Nhập tên văn bản'} />
          </Form.Item>

          <Form.Item
            name={'document_number'}
            label={'Số quyết định ban hành'}
            rules={[
              {
                required: true,
                message: 'Vui lòng nhập số quyết định ban hành',
              },
              { validator: validator.validateInputString },
            ]}>
            <Input placeholder={'Nhập số quyết định ban hành'} />
          </Form.Item>

          <Form.Item
            name={'issue_date'}
            label={'Ngày ban hành'}
            rules={[
              {
                required: true,
                message: 'Vui lòng chọn ngày ban hành',
              },
            ]}>
            <DatePicker style={{ width: '100%' }} format={DATE_FORMAT_LIST} />
          </Form.Item>

          <Form.Item
            name={'issue_level'}
            label={'Phân loại'}
            rules={[{ required: true, message: 'Vui lòng chọn phân loại' }]}>
            <Select
              allowClear
              placeholder={'Chọn phân loại'}
              optionFilterProp={'label'}>
              {ISSUE_LEVEL_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value} label={opt.label}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* <Form.Item name={'contruction_board'} label={'Ban chủ trì xây dựng'}>
            <Input placeholder={'Nhập ban chủ trì xây dựng'} />
          </Form.Item> */}

          <Form.Item name={'status'} label={'Trạng thái'}>
            <Select
              showSearch
              allowClear
              placeholder={'Trạng thái'}
              filterOption={true}
              optionFilterProp={'name'}>
              {[
                { label: 'Đã bãi bỏ', value: 'ABOLISHED' },
                { label: 'Hết hiệu lực', value: 'EXPIRED' },
                { label: 'Còn hiệu lực', value: 'ACTIVE' },
              ].map(({ label, value }) => (
                <Option key={value} name={label} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name={'file_id'}
            label={'Tài liệu đính kèm'}
            rules={[{ required: true, message: 'Vui lòng thêm tài liệu' }]}>
            <Dragger
              multiple
              valuePropName={'fileList'}
              fileList={fileList}
              onChange={handleChangeFile}
              beforeUpload={handleAddFileToUpload}>
              <DraggerUploadStyled>
                <CloudUploadOutlined />
                <span>Kéo thả hoặc click vào để tải lên tài liệu</span>
              </DraggerUploadStyled>
            </Dragger>
          </Form.Item>

          <FormActionFooter>
            <Button onClick={handleCancel}>Huỷ bỏ</Button>
            <Button
              style={{ marginLeft: 10 }}
              type={'primary'}
              htmlType={'submit'}>
              Cập nhật quyết định
            </Button>
          </FormActionFooter>
        </Form>
      </Container>
    </ContentBlockWrapper>
  )

  return (
    <DashboardLayout>
      <Helmet>
        <title>Chi tiết quyết định nghị quyết</title>
      </Helmet>
      <PageTitle
        location={props.location}
        title={'Chi tiết quyết định nghị quyết'}
        routerGoback={'/board-resolution-minutes-hdqt'}
      />
      <ViewDetailContent>{renderFormContent()}</ViewDetailContent>
    </DashboardLayout>
  )
}

BoardResolutionMinutesUpdate.propTypes = {}

export default memo(
  inject(
    'loadingAnimationStore',
    'commonStore',
    'fileStore',
    'boardResolutionMinutesStore'
  )(observer(BoardResolutionMinutesUpdate))
)
